/**
 * Dialog, menu and field surfaces for the app, in design tokens.
 *
 * These are class strings rather than new defaults on the radix primitives
 * because those primitives are also used by the marketing pages (the SEO
 * converters and the downloads switcher), which keep their own look. Importing
 * a surface is what opts a component in, so the two never have to agree.
 *
 * Before this existed every modal rolled its own — `from-cyan-50 to-steel-120`,
 * `bg-white/80 backdrop-blur-md`, `dark:bg-navy-880`, `dark:bg-slate-900`, and
 * three different radii. Reach for these instead of writing another.
 */

/** Centred modal panel. Pair with layout/size classes at the call site. */
export const dialogSurface =
  'v2-panel-surface rounded-[var(--v2-card-radius)] border-[length:var(--v2-border-width)] border-[var(--v2-card-border)] text-[var(--v2-text-primary)] shadow-[0_24px_64px_-16px_rgb(0_0_0/0.28)]';

/** Dropdown, select and popover panel. */
export const menuSurface =
  'v2-panel-surface rounded-[var(--v2-card-radius)] border-[length:var(--v2-border-width)] border-[var(--v2-card-border)] p-1 text-[var(--v2-text-primary)] shadow-[0_18px_48px_-12px_rgb(0_0_0/0.24)]';

/**
 * A row inside `menuSurface`. Covers both radix highlight attributes:
 * dropdown items use `data-[highlighted]`, command items `data-[selected]`.
 */
export const menuItemSurface =
  'flex cursor-pointer items-center gap-2.5 rounded-[var(--v2-button-radius)] px-3 py-2 text-[13px] text-[var(--v2-text-primary)] transition-colors focus:bg-black/[0.05] data-[highlighted]:bg-black/[0.05] data-[selected=true]:bg-black/[0.05] dark:focus:bg-white/10 dark:data-[highlighted]:bg-white/10 dark:data-[selected=true]:bg-white/10';

/** Text input / trigger shell. */
export const fieldSurface =
  'rounded-[var(--v2-button-radius)] border-[length:var(--v2-border-width)] border-[var(--v2-sidebar-border)] bg-[var(--v2-page-bg)] text-[var(--v2-text-primary)] placeholder:text-[var(--v2-text-muted)]';

/** Inset panel inside a dialog — a well, not a second card. */
export const wellSurface =
  'rounded-[var(--v2-card-radius)] border-[length:var(--v2-border-width)] border-[var(--v2-card-border)] bg-[var(--v2-card-bg)]';

/** Primary action. */
export const primaryButtonSurface =
  'rounded-[var(--v2-button-radius)] bg-[var(--v2-cta-bg)] text-[var(--v2-cta-text)] transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50';

/** Secondary action, sat beside a primary one. */
export const secondaryButtonSurface =
  'rounded-[var(--v2-button-radius)] border-[length:var(--v2-border-width)] border-[var(--v2-sidebar-border)] bg-transparent text-[var(--v2-text-primary)] transition-colors hover:bg-black/[0.03] disabled:pointer-events-none disabled:opacity-50 dark:hover:bg-white/5';

export const toolbarButtonSurface =
  'flex h-8 shrink-0 items-center gap-1.5 rounded-[var(--v2-button-radius)] px-2.5 text-[13px] font-medium text-[var(--v2-text-primary)] transition-colors hover:bg-black/[0.05] max-md:w-8 max-md:justify-center max-md:px-0 dark:hover:bg-white/[0.08]';

export const toolbarButtonActiveSurface =
  'bg-[color-mix(in_srgb,var(--v2-accent)_12%,transparent)] text-[var(--v2-accent)] hover:bg-[color-mix(in_srgb,var(--v2-accent)_16%,transparent)]';
